import styled from 'styled-components';
import Slide from '../containers/Slide';
import AccentLine from '../widgets/AccentLine';
import GetEducationList from '../../data/education-list';
import { motion, useInView } from "framer-motion"
import React, { useRef } from 'react';
import SectionHeader from '../widgets/SectionHeader';
import BathroomImage from '../../res/images/bathrooms/img8.jpeg';
import ShowerImage from '../../res/images/bathrooms/img11.jpeg';
import FlooringImage from '../../res/images/flooring/img29.jpeg';
import TileImage from '../../res/images/bathrooms/img2.jpeg';

const Container = styled.div.attrs({ className: "services-slide" })`
    width: 75%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    padding: 0 0 6rem 0;

    #services-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 2rem;

        h3 {
            color: var(--white);
            font-size: 2.4rem;
            font-weight: normal;
        }

        p {
            box-sizing: border-box;
            font-size: 1.2rem;
            line-height: 1.8rem;
            color: var(--muted);
        }

        & > p {
            border-left: 4px solid var(--yellow);
            padding: 0 1rem;
        }

        .cards {
            display: grid;
            grid-template-columns: repeat(12, 1fr);
            gap: 2rem;
            margin: 22px 0;

            .card {
                grid-column: span 4;
                background: var(--surface);
                border: 1px solid var(--border);
                border-radius: var(--radius-lg);
                overflow: hidden;
                box-shadow: var(--shadow-soft);
                transition: transform .12s ease;
                min-height: 260px;
                display: flex;
                flex-direction: column;
            }

            .card .media {
                height: 150px;
                background: radial-gradient(300px 160px at 30% 30%, rgba(255, 255, 255, .55), transparent 60%), linear-gradient(135deg, 
            color-mix(in srgb, var(--brand) 16%, white), 
            color-mix(in srgb, var(--brand-2) 10%, white));
                position: relative;
            }

            .card .media::after {
                content: "";
                position: absolute;
                inset: 0;
                //background: linear-gradient(180deg, transparent 40%, rgba(15, 35, 42, .08)), radial-gradient(500px 250px at 80% 0%, rgba(14, 134, 204, .28), transparent 65%);
                background-position: center;
                background-size: cover;
                background-repeat: no-repeat;
                opacity: .9;
            }

            .card:nth-of-type(1) .media::after {
                background-image: url('${BathroomImage}');
            }

            .card:nth-of-type(2) .media::after {
                background-image: url('${TileImage}');
            }

            .card:nth-of-type(3) .media::after {
                background-image: url('${FlooringImage}');
            }

            .card .body {
                padding: 16px;
                display: grid;
                gap: 8px;
            }

            .card .body h3 {
                margin: 0;
                font-size: 18px;
                letter-spacing: -.2px;
                color: var(--black);
            }

            .card .body p {
                margin: 0;
                color: var(--muted);
                font-size: 14px;
            }
        }
    }

    @media screen and (max-width: 980px) {
        height: auto;

        #services-container .cards {
            grid-template-columns: 1fr 1fr 1fr;
        }
    }

    @media screen and (max-width: 650px) {
        height: auto;

        #education-list {
            grid-template-columns: 1fr;
            grid-template-rows: auto;
        }
    }
`

export default function ServicesSlide() {

    /*
        The animation is conditional on the window's innerWidth (in coordination with the CSS media queries).
        If desktop, stagger the li's via the parent.
        If mobile, the parent stagger is discarded, and each li is responsible for its own animation.

        This was done to give that 'each item appears individually' feel.
    */

    const liAnim = {
        hidden: { opacity: 0, left: '100%' },
        visible: { opacity: 1, left: '0%' }
    }

    // One ref and useInView for each education item
    const liRef1 = useRef(null);
    const liInView1 = useInView(liRef1, { once: true });

    const liRef2 = useRef(null);
    const liInView2 = useInView(liRef2, { once: true });

    const liRef3 = useRef(null);
    const liInView3 = useInView(liRef3, { once: true });

    const liRef4 = useRef(null);
    const liInView4 = useInView(liRef4, { once: true });

    const liRef5 = useRef(null);
    const liInView5 = useInView(liRef5, { once: true });

    const liRef6 = useRef(null);
    const liInView6 = useInView(liRef6, { once: true });

    const liRefArr = [
        { ref: liRef1, inView: liInView1 },
        { ref: liRef2, inView: liInView2 },
        { ref: liRef3, inView: liInView3 },
        { ref: liRef4, inView: liInView4 },
        { ref: liRef5, inView: liInView5 },
        { ref: liRef6, inView: liInView6 }
    ];

    // One ref and useInView for each education item
    const ulRef = useRef(null);
    const ulInView = useInView(ulRef, { once: true })

    return (
        <Slide color="var(--brand-3);" height="auto" freeHeightBreakpoint="980px">

            <Container>

                <SectionHeader title="Services" id="services-section" />

                <div id='services-container'>

                    <h3>
                        Bathroom Remodeling & Flooring You'll Love Coming Home To
                    </h3>

                    <p>
                        From bold tilework to clean modern vanities, we blend design and craftsmanship for a finish that
                        feels premium and lasts.
                    </p>

                    <div class="cards">
                        <article class="card">
                            <div class="media" aria-hidden="true"></div>
                            <div class="body">
                                <h3>Full Bathroom Remodels</h3>
                                <p>Layout refreshes, showers, vanities, lighting, and modern finishes—start to finish.</p>
                            </div>
                        </article>

                        <article class="card">
                            <div class="media" aria-hidden="true"></div>
                            <div class="body">
                                <h3>Tile &amp; Shower Installation</h3>
                                <p>Waterproofing, niches, Schluter trims, grout lines, and crisp alignment.</p>
                            </div>
                        </article>

                        <article class="card">
                            <div class="media" aria-hidden="true"></div>
                            <div class="body">
                                <h3>Flooring Installation</h3>
                                <p>LVP, tile, laminate, transitions, and baseboards—done clean and level.</p>
                            </div>
                        </article>

                    </div>

                    <AccentLine width="150px" color='var(--yellow)' />
                    <AccentLine width="75px" color='var(--yellow)' />

                </div>

            </Container>

        </Slide>
    )
}